import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { authApi, type AuthUser } from "./api";

interface AuthState {
  loading: boolean;
  user: AuthUser | null;
  registrationEnabled: boolean;
  needsOnboarding: boolean;
  emailVerified: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthCtx = createContext<AuthState>({
  loading: true,
  user: null,
  registrationEnabled: false,
  needsOnboarding: false,
  emailVerified: false,
  refresh: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  async function refresh() {
    try {
      const r = await authApi.me();
      setUser(r.user);
      setRegistrationEnabled(r.registration_enabled);
      setNeedsOnboarding(Boolean(r.user) && r.needs_onboarding);
      setEmailVerified(Boolean(r.user) && r.email_verified);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function signOut() {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    setUser(null);
  }

  return (
    <AuthCtx.Provider
      value={{ loading, user, registrationEnabled, needsOnboarding, emailVerified, refresh, signOut }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { loading, user } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-parchment-muted text-sm">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
